import { FloatingOverlay } from '@floating-ui/react';
import styled from '@emotion/styled';
import { css, Global } from '@emotion/react';
import { boxShadow, overlayColor } from '../../helpers/styleHelpers';
import { breakpoint } from '../../../themes/stena-recycling/breakpoint';
import { spacing } from '../../../themes/stena-recycling/spacing';
import { colors } from '../../../themes/stena-recycling/colors';

const getModalMaxHeight = ({
  fullScreen,
  constrainToViewport,
}: {
  fullScreen?: boolean;
  constrainToViewport?: boolean;
}) => {
  if (fullScreen) {
    return '100%';
  }

  if (constrainToViewport) {
    return `calc(100dvh - (${spacing.mediumLow} * 2))`;
  }

  return 'unset';
};

export const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: ${spacing.xxsmall};
`;

export function PrintGlobals() {
  return (
    <Global
      styles={css`
        @media print {
          /* Use A4, adjust margins for Chrome print scaling */
          @page {
            size: A4 portrait;
            margin: 12mm;
          }

          /* Hide the app shell so only the modal prints */
          body #root {
            display: none !important;
          }

          /* Prevent weird zoom by giving the portal a normal block formatting context */
          #floating-ui-portal-root {
            position: static !important;
            inset: auto !important;
            transform: none !important;
            overflow: visible !important;
            width: auto !important;
            max-width: 100% !important;
          }
        }
      `}
    />
  );
}

export const ModalInner = styled.div<{ topPadding?: boolean }>`
  box-sizing: border-box;
  flex-grow: 1;
  padding: 0 ${spacing.mediumLow};
  margin-bottom: ${spacing.mediumLow};
  width: 100%;
  overflow: visible;
  ${({ topPadding }) => topPadding && `padding-top: ${spacing.mediumLow}`};

  @media (max-width: ${breakpoint.medium}px) {
    padding: 0 ${spacing.small};
    margin-bottom: ${spacing.small};
    max-width: 100vw;
    ${({ topPadding }) => topPadding && `padding-top: ${spacing.small}`};
  }
`;

export const ModalScrollableContent = styled.div<{ constrainToViewport?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;

  ${({ constrainToViewport }) =>
    constrainToViewport &&
    `
      flex: 1 1 auto;
      overflow-y: auto;
      overscroll-behavior: contain;
    `}
`;

export const ModalBody = styled.div<{
  fullScreen?: boolean;
  maxWidth?: string;
  minWidth?: string;
  constrainToViewport?: boolean;
}>`
  display: flex;
  flex-direction: column;
  margin: ${spacing.mediumLow};
  background-color: ${colors.base.white};

  width: ${({ fullScreen }) => (fullScreen ? '100%' : 'min-content')};
  ${({ fullScreen }) => fullScreen && 'height: 100%'};
  min-width: ${({ minWidth }) => minWidth ?? '650px'};
  box-shadow: ${boxShadow};
  border-radius: 8px;
  max-width: ${({ maxWidth }) => maxWidth ?? 'unset'};
  overflow: ${({ constrainToViewport }) => (constrainToViewport ? 'hidden' : 'visible')};
  max-height: ${getModalMaxHeight};

  @media (max-width: ${breakpoint.medium}px) {
    width: 100%;
    min-width: unset;
    min-height: 100svh;
    max-height: ${({ constrainToViewport }) => (constrainToViewport ? '100svh' : 'unset')};
    align-self: flex-start;
    border-radius: 0;
    margin: 0;
  }

  @media print {
    box-shadow: none !important;
    width: auto !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: auto !important;
    border-radius: 0 !important;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  padding: ${spacing.mediumLow};
  gap: ${spacing.xsmall};

  @media (max-width: ${breakpoint.small}px) {
    flex-direction: column-reverse;
    align-items: stretch;
    padding-bottom: ${spacing.small};
    padding-left: ${spacing.small};
    padding-right: ${spacing.small};
  }

  @media print {
    display: none !important;
  }
`;

export const OverlayWrapper = styled(FloatingOverlay)`
  background-color: ${overlayColor};
  z-index: 100;
  display: grid;
  place-items: center;
  visibility: visible !important;

  @media print {
    position: static !important; /* avoid fixed/absolute in print */
    inset: auto !important;
    transform: none !important;
    overflow: visible !important;
    width: auto !important;
    max-width: 100% !important;
  }
`;

export const HideOnPrint = styled.div`
  @media print {
    display: none !important;
  }
`;
